import type { ComputedRef, Ref } from 'vue';
import { unref, onMounted, computed, ref, watch } from 'vue';
import type { MaybeRef } from '@vueuse/core';
import type { ObjectSchema, ValidationError } from 'yup';
import { defaultValidationOptions } from '../constants/default-validation-options';
import type { ValidationResult } from '../types/validation-result';
import type { ValidationOptions } from '../types/validation-options';

export const useValidationSchema = <TValue = unknown>(
  value: MaybeRef<TValue>,
  rules?: ObjectSchema<TValue>,
  validationOptions: MaybeRef<ValidationOptions> = defaultValidationOptions
) => {
  type Rule = keyof typeof rules.fields;
  type Result = Record<Rule, ValidationResult>;

  const getResultFromValidationErrors = (
    key: string,
    validationErrors: ValidationError[]
  ): ValidationResult => {
    const errors = validationErrors
      .filter(validationError => validationError.path === key)
      .map(({ message }) => message);
    return {
      errors,
      errorMessage: errors[0] || '',
      validated: true,
      valid: !errors.length,
    };
  };
  const defaultValidateReturn = { errorMessage: '', valid: false, validated: false, errors: [] };

  const getDefaultResults = (data?: Partial<ValidationResult>) => {
    const newResult = {};
    Object.keys(rules.fields).forEach(key => {
      unref(newResult)[key] = { ...defaultValidateReturn, ...data };
    });
    return newResult as Result;
  };

  const options: ComputedRef<ValidationOptions> = computed(() => ({
    ...defaultValidationOptions,
    ...unref(validationOptions),
  }));
  const result = ref<Result>(getDefaultResults()) as Ref<Result>;
  const valid = computed(() => Object.keys(result.value).every(key => result.value[key].valid));

  const validateAll = () => {
    try {
      rules.validateSync(unref(value), { abortEarly: false });
      result.value = getDefaultResults({ validated: true, valid: true });
    } catch (e) {
      const newResult = getDefaultResults();
      Object.keys(rules.fields).forEach(key => {
        unref(newResult)[key] = getResultFromValidationErrors(key, e.inner);
      });
      result.value = newResult;
    }
    return result.value;
  };

  const validate = (key: Rule, onlySuccess = false) => {
    try {
      rules.validateSyncAt(key as string, unref(value), {
        abortEarly: false,
      });
      result.value = {
        ...result.value,
        [key]: { ...defaultValidateReturn, valid: true, validated: true },
      };
    } catch (e) {
      if (onlySuccess) return;
      result.value = {
        ...result.value,
        [key]: getResultFromValidationErrors(key as string, e.inner),
      };
    }
    return result.value[key];
  };

  const resetAll = () => {
    result.value = getDefaultResults();
  };
  const reset = (key: Rule) => {
    result.value = {
      ...result.value,
      [key]: defaultValidateReturn,
    };
  };

  Object.keys(rules.fields).forEach(rule => {
    watch(
      () => ref(value).value[rule],
      () => {
        if (options.value.resetOnUpdate) {
          reset(rule as Rule);
        }
        if (options.value.validateSuccessOnUpdate) {
          validate(rule as Rule, true);
          return;
        }
        if (options.value.validateOnUpdate) {
          validate(rule as Rule);
        }
      }
    );
  });

  onMounted(() => {
    if (options.value.validateOnMount) {
      validateAll();
    }
  });

  return {
    valid,
    result: computed<Result>(() => result.value),
    validate,
    validateAll,
    resetAll,
    reset,
  };
};
